import React, { useEffect, useState } from 'react';
import { StudentAppLayout } from '../../components/StudentAppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { CheckCircle, Clock, BookOpen, Calendar, Timer, Loader2 } from 'lucide-react';
import { useLazyQuery, useEvent } from '@soap/modules';
import { useNavigate } from 'react-router';
import { useFormatTimerString } from '../../../hooks/useFormatTimerString';
import { useFormatDisplayDate } from '../../../utils/useFormatDisplayDate';

interface AssessmentDto {
  e265_AssessmentId: string;
  e265_AssessmentTitle: string;
  e265_Description: string;
  e265_SharedAssessmentId: string;
  e265_ShareType: string;
  e265_AssessmentResultId?: string;
  e265_Status: string | null;
  e265_Score?: number;
  e265_CompletedAt?: string;
  e265_LinkUrl?: string;
  e265_DueDate?: string;
  e265_EstimatedTimeSeconds?: number;
}

export function StudentDashboardPage() {
  const formatDate = useFormatDisplayDate();
  const [assessments, setAssessments] = useState<AssessmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { fromSeconds } = useFormatTimerString();
  
  // Get user profile to extract StudentId
  const [getUserProfile] = useLazyQuery("C166v1_GetUserProfile");
  const [getStudentAssessments] = useLazyQuery("C277v1_GetStudentAssessments");

  useEvent({
    eventName: "E138v1_GotUserProfile",
    onEventReceived: (e138: { e138_StudentId?: string }) => {
      if (e138.e138_StudentId) {
        setStudentId(e138.e138_StudentId);
        getStudentAssessments({ C277_StudentId: e138.e138_StudentId });
      } else {
        // Student user doesn't have StudentId linked, show empty state
        setStudentId(null);
        setAssessments([]);
        setLoading(false);
      }
    }
  });

  useEvent({
    eventName: "E265v1_GotStudentAssessments",
    onEventReceived: (event: { e265_Assessments: AssessmentDto[] }) => {
      setAssessments(event.e265_Assessments || []);
      setLoading(false);
    }
  });

  // Listen for assessment cancelled event (when assessment is deleted from unit)
  useEvent({
    eventName: "E169v1_AssessmentCancelled",
    onEventReceived: () => {
      // Refresh assessments when an assessment is deleted from a unit
      // The backend query filters by student, so this is safe
      if (studentId) {
        getStudentAssessments({ C277_StudentId: studentId });
      }
    }
  });

  // Listen for unit deleted event (when unit with assessment is deleted)
  useEvent({
    eventName: "E255v1_UnitDeleted",
    onEventReceived: () => {
      // Refresh assessments when a unit with an assessment is deleted
      // The backend query filters by student, so this is safe
      if (studentId) {
        getStudentAssessments({ C277_StudentId: studentId });
      }
    }
  });

  // Listen for assessment shared event (only react when shared by student)
  useEvent({
    eventName: "E166v1_AssessmentShared",
    onEventReceived: (event: { e166_ShareTypeKey?: string }) => {
      // Only refresh if this is a ByStudent share (affects student dashboard)
      // The backend query filters by student and ShareType, so this is safe
      if (event.e166_ShareTypeKey === "by-student" && studentId) {
        getStudentAssessments({ C277_StudentId: studentId });
      }
    }
  });

  // Listen for shared assessment link revoked event (when ByStudent share is cancelled)
  useEvent({
    eventName: "E168v1_SharedAssessmentLinkRevoked",
    onEventReceived: () => {
      // Refresh assessments when a ByStudent share is cancelled
      // Backend only broadcasts this event for ByStudent shares, so safe to refresh
      // The backend query filters by student and ShareType, so this is safe
      if (studentId) {
        getStudentAssessments({ C277_StudentId: studentId });
      }
    }
  });

  useEffect(() => {
    setLoading(true);
    getUserProfile();
  }, [getUserProfile]);

  const getStatusIcon = (status: string | null | undefined) => {
    if (!status) return <BookOpen className="h-5 w-5 text-yellow-500" />;
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-confirm-action" />;
      case 'started':
        return <Clock className="h-5 w-5 text-action-blue" />;
      case 'needs-marking':
        return <Clock className="h-5 w-5 text-orange-500" />;
      case 'abandoned':
        return <Clock className="h-5 w-5 text-gray-500" />;
      default:
        return <BookOpen className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    if (!status) return 'bg-yellow-100 text-yellow-800';
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-confirm-action/15 text-confirm-action-dark';
      case 'started':
        return 'bg-action-blue/15 text-action-blue-dark';
      case 'needs-marking':
        return 'bg-orange-100 text-orange-800';
      case 'abandoned':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusLabel = (status: string | null | undefined) => {
    if (!status) return 'Pending';
    switch (status.toLowerCase()) {
      case 'completed':
        return 'Completed';
      case 'started':
        return 'In Progress';
      case 'needs-marking':
        return 'Needs Marking';
      case 'abandoned':
        return 'Abandoned';
      default:
        return 'Pending';
    }
  };

  const handleStartAssessment = (assessment: AssessmentDto) => {
    if (assessment.e265_SharedAssessmentId) {
      // Navigate to assessment taking page using the correct route
      navigate(`/take-assessment/${assessment.e265_SharedAssessmentId}`);
    }
  };

  if (loading) {
    return (
      <StudentAppLayout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-action-blue" />
        </div>
      </StudentAppLayout>
    );
  }

  return (
    <StudentAppLayout>
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Student Dashboard</h1>
          <p className="mt-2 text-gray-600">Your assigned assessments and learning materials</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {assessments.map((assessment) => (
            <Card key={assessment.e265_AssessmentId} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(assessment.e265_Status)}
                    <CardTitle className="text-lg">{assessment.e265_AssessmentTitle}</CardTitle>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(assessment.e265_Status)}`}>
                    {getStatusLabel(assessment.e265_Status)}
                  </span>
                </div>
                {assessment.e265_Description && (
                  <CardDescription>{assessment.e265_Description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assessment.e265_DueDate && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      Due: {formatDate(assessment.e265_DueDate)}
                    </div>
                  )}
                  {assessment.e265_EstimatedTimeSeconds && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Timer className="h-4 w-4 mr-2" />
                      Estimated time: {fromSeconds(assessment.e265_EstimatedTimeSeconds)}
                    </div>
                  )}
                  {assessment.e265_Status === 'completed' && assessment.e265_Score != null && (
                    <div className="flex items-center text-sm font-semibold text-gray-900">
                      Score: {assessment.e265_Score}%
                    </div>
                  )}
                  <div className="pt-2">
                    <Button 
                      className="w-full" 
                      variant={assessment.e265_Status === 'completed' ? 'outline' : 'default'}
                      disabled={assessment.e265_Status === 'completed'}
                      onClick={() => handleStartAssessment(assessment)}
                    >
                      {assessment.e265_Status === 'completed' 
                        ? 'View Results' 
                        : assessment.e265_Status === 'started'
                        ? 'Continue Assessment'
                        : 'Start Assessment'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {assessments.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No assessments assigned</h3>
              <p className="text-gray-600">Check back later for new assignments from your teachers.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </StudentAppLayout>
  );
}